/**
 * The member hub's Stats tab (Member Stats design "Stats tab" boards 1a–1i, contract 2026-10-11)
 * — the pure rules behind `components/community/MemberStats`: month arithmetic, the bar rules,
 * the range pills, the quiet-month folding of the ledger and every sentence. No fetch, no React;
 * unit-tested in `tests/community-member-stats.spec.ts`.
 *
 * Stats are PUBLIC (contract §0.1): the visitor and the member read the same numbers. `own` only
 * changes the person (second vs third) and adds the coaching (CTAs, next badge, rank hint, the
 * photo note) — and `own` is true only after hydration, so the shared-cached HTML is the visitor
 * page (Constraint 35cf845b).
 *
 * Dates: every key is DealHunt's UTC text ("YYYY-MM", "YYYY-MM-DD") parsed BY HAND with a fixed
 * month table — never `new Date("YYYY-MM")` (local-time trap) and never `Intl` (the labels are in
 * shared-cached HTML, Constraint bb38c2ea). No clock is read: "today" is the payload's last day.
 */
import {
  BADGE_LOOKS,
  badgeProgressRows,
  closestBadges,
  sentCount,
  tierProgress,
  tileLetters,
  type BadgeCap,
} from './model'
import { closestPinned, toNextTierLine } from './hub'
import type { OwnerCommunityMemberStats } from 'lib/dealhunt/types'
import type { CommunityBadge, CommunityBadgeId, CommunityStanding } from './types'

type Stats = OwnerCommunityMemberStats
type StatsReview = Stats['reviews'][number]
type StatsMonth = Stats['months'][number]

// ── payload guard ─────────────────────────────────────────────────────────────

/**
 * DealHunt's stats payload, or null when it is not one (an older DealHunt, an error body, another
 * member's): the tab then says "Stats aren't available yet" instead of throwing while rendering.
 * Shape only — every figure is still read null-safely below.
 */
export function memberStatsOf(raw: unknown, handle: string): Stats | null {
  if (typeof raw !== 'object' || raw === null) {
    return null
  }
  const s = raw as Partial<Stats>
  const ok =
    typeof s.handle === 'string' &&
    s.handle.toLowerCase() === handle.toLowerCase() &&
    parseMonth(s.month) !== null &&
    parseMonth(s.since) !== null &&
    typeof s.readers === 'object' &&
    s.readers !== null &&
    Array.isArray(s.readers.days) &&
    Array.isArray(s.readers.months) &&
    typeof s.reading_time === 'object' &&
    s.reading_time !== null &&
    Array.isArray(s.reviews) &&
    typeof s.points === 'object' &&
    s.points !== null &&
    Array.isArray(s.months) &&
    typeof s.community === 'object' &&
    s.community !== null
  return ok ? (raw as Stats) : null
}

// ── UTC months and days, by hand ──────────────────────────────────────────────

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/** "2026-10" → { year: 2026, month: 9 } (month 0–11); null for anything else. */
export function parseMonth(key: unknown): { year: number; month: number } | null {
  if (typeof key !== 'string') {
    return null
  }
  const m = /^(\d{4})-(\d{2})$/.exec(key)
  if (!m) {
    return null
  }
  const month = Number(m[2]) - 1
  return month >= 0 && month <= 11 ? { year: Number(m[1]), month } : null
}

/** "2026-10-05" (or an ISO instant "2026-10-05T…Z") → its UTC date parts; null otherwise. The
 *  instant's first ten characters ARE its UTC date — DealHunt sends ISO in UTC (`…Z`). */
export function parseDay(key: unknown): { year: number; month: number; day: number } | null {
  if (typeof key !== 'string') {
    return null
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|T.*Z$)/.exec(key)
  if (!m) {
    return null
  }
  const month = Number(m[2]) - 1
  const day = Number(m[3])
  return month >= 0 && month <= 11 && day >= 1 && day <= 31
    ? { year: Number(m[1]), month, day }
    : null
}

function monthKeyOf(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`
}

/** The month key `n` months after `key` (negative = before); null for an unreadable key. */
export function addMonths(key: string, n: number): string | null {
  const p = parseMonth(key)
  if (!p) {
    return null
  }
  const index = p.year * 12 + p.month + n
  return monthKeyOf(Math.floor(index / 12), ((index % 12) + 12) % 12)
}

/** Whole months from `from` to `to` ("2026-09" → "2027-03" = 6); null for an unreadable key. */
export function monthsBetween(from: string, to: string): number | null {
  const a = parseMonth(from)
  const b = parseMonth(to)
  return a && b ? b.year * 12 + b.month - (a.year * 12 + a.month) : null
}

/** The month key of a day or an ISO instant ("2026-10-05T…Z" → "2026-10"); null otherwise. */
export function monthOfDay(day: string | null | undefined): string | null {
  const p = parseDay(day)
  return p ? monthKeyOf(p.year, p.month) : null
}

export type MonthForm = 'long' | 'short' | 'letter' | 'shortYear' | 'longYear'

/** "October", "Oct", "O", "Oct 2026", "October 2026" — from the fixed table; '' when unreadable. */
export function monthLabel(key: string, form: MonthForm = 'short'): string {
  const p = parseMonth(key)
  if (!p) {
    return ''
  }
  const long = MONTH_NAMES[p.month]
  switch (form) {
    case 'long':
      return long
    case 'letter':
      return long.charAt(0)
    case 'shortYear':
      return `${long.slice(0, 3)} ${p.year}`
    case 'longYear':
      return `${long} ${p.year}`
    default:
      return long.slice(0, 3)
  }
}

/** "Sep 28" from "2026-09-28" or an ISO instant; with the year, "Sep 18, 2026". '' when
 *  unreadable. */
export function dayLabel(day: string | null | undefined, withYear = false): string {
  const p = parseDay(day)
  if (!p) {
    return ''
  }
  const label = `${MONTH_NAMES[p.month].slice(0, 3)} ${p.day}`
  return withYear ? `${label}, ${p.year}` : label
}

/** "Apr – Jun 2027", "Nov 2026 – Feb 2027": a span of months, the year said once when shared. */
export function monthSpanLabel(from: string, to: string): string {
  if (from === to) {
    return monthLabel(from, 'shortYear')
  }
  const a = parseMonth(from)
  const b = parseMonth(to)
  if (!a || !b) {
    return ''
  }
  return a.year === b.year
    ? `${monthLabel(from)} – ${monthLabel(to, 'shortYear')}`
    : `${monthLabel(from, 'shortYear')} – ${monthLabel(to, 'shortYear')}`
}

// ── numbers ───────────────────────────────────────────────────────────────────

/** "5,807"; "—" for an unknown figure. */
export function fmtCount(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? Math.round(n).toLocaleString('en-US') : '—'
}

/** "1 owner review", "2 owner reviews". */
export function plural(n: number, one: string, many: string): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`
}

/** "1m 12s", "1m 05s", "49s"; null for no figure (fewer than 5 timed readers). */
export function fmtSeconds(seconds: number | null | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return null
  }
  const s = Math.round(seconds)
  if (s < 60) {
    return `${s}s`
  }
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
}

/** "~118 / day" from DealHunt's `per_day` (1 decimal); "under 1 / day" below one; null unknown. */
export function perDay(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null
  }
  if (value === 0) {
    return '0 / day'
  }
  if (value < 1) {
    return 'under 1 / day'
  }
  return `~${Math.round(value).toLocaleString('en-US')} / day`
}

/** "about 700": a typical figure said loosely — tens under 100, fifties under 1,000, then
 *  hundreds. */
export function roughly(n: number): number {
  let step = 100
  if (n < 100) {
    step = 10
  } else if (n < 1000) {
    step = 50
  }
  return Math.max(step, Math.round(n / step) * step)
}

// ── range pills ───────────────────────────────────────────────────────────────

export type RangeId = '6m' | '12m' | 'all'

export interface StatsRange {
  /** 6m / 12m / All appear only after 6 months of history — before, they would all draw the same
   *  bars (design note). */
  pills: boolean
  options: ReadonlyArray<{ id: RangeId; label: string }>
  /** The pill the server renders selected (design 1d: 12m). */
  initial: RangeId
  /** "Since Sep 2026" — the chip shown instead of the pills. */
  sinceLabel: string
}

export const RANGE_OPTIONS: ReadonlyArray<{ id: RangeId; label: string }> = [
  { id: '6m', label: '6m' },
  { id: '12m', label: '12m' },
  { id: 'all', label: 'All' },
]

/** The pills from 6 whole months between `since` and the current month; else the chip. */
export function rangeFor(since: string, month: string): StatsRange {
  const span = monthsBetween(since, month)
  const pills = span !== null && span >= 6
  return {
    pills,
    options: pills ? RANGE_OPTIONS : [],
    initial: pills ? '12m' : 'all',
    sinceLabel: `Since ${monthLabel(since, 'shortYear')}`,
  }
}

/** The last 6 / 12 entries, or all of them. */
export function sliceRange<T>(list: readonly T[], range: RangeId): T[] {
  if (range === 'all') {
    return list.slice()
  }
  return list.slice(-(range === '6m' ? 6 : 12))
}

// ── bars ──────────────────────────────────────────────────────────────────────

/** The drawn height of the tallest bar, px (design: 104 of a 136 px plot). */
export const BAR_MAX_PX = 104

export interface BarInput {
  key: string
  /** The axis label under the bar ("Oct", "O", or '' for days). */
  axis: string
  value: number | null
  /** The running month / today — drawn light, its figure goes in the header ("so far"). */
  cur?: boolean
}

export interface Bar {
  key: string
  axis: string
  value: number | null
  /** Not recorded: a striped full-height column, never a zero. */
  isNull: boolean
  zero: boolean
  cur: boolean
  /** px; 0 for a null (the stripes fill the column instead). */
  height: number
  tone: 'bar' | 'cur' | 'zero'
  /** The figure above the bar: the tallest one only, never the running one, and only when the
   *  tallest is above 1 (one reader is no peak). */
  showLabel: boolean
  label: string
  align: 'start' | 'center' | 'end'
}

/** The design's bar rules (renderVals `bars`): one hue, a 2 px grey stub for zero, at least 6 px
 *  for any reader, null striped. */
export function bars(list: readonly BarInput[], maxH = BAR_MAX_PX): Bar[] {
  const values = list.map(x => x.value).filter((v): v is number => typeof v === 'number')
  const max = Math.max(1, ...values)
  return list.map((x, i) => {
    const isNull = typeof x.value !== 'number'
    const value = isNull ? null : (x.value as number)
    const zero = value === 0
    const cur = x.cur === true
    const isMax = value !== null && value === max && max > 1
    let height = 0
    if (value !== null) {
      height = zero ? 2 : Math.max(6, Math.round((value / max) * maxH))
    }
    let tone: Bar['tone'] = 'bar'
    if (zero) {
      tone = 'zero'
    } else if (cur) {
      tone = 'cur'
    }
    let align: Bar['align'] = 'center'
    if (i === list.length - 1) {
      align = 'end'
    } else if (i === 0) {
      align = 'start'
    }
    return {
      key: x.key,
      axis: x.axis,
      value,
      isNull,
      zero,
      cur,
      height,
      tone,
      showLabel: !isNull && !zero && isMax && !cur,
      label: cur ? `${fmtCount(value)} so far` : fmtCount(value),
      align,
    }
  })
}

/** The per-month bars for the chosen range: three-letter labels up to 6 bars, single letters
 *  beyond (design 1d / 1h — 12 letters fit a 360 px phone). */
export function monthBars(stats: Stats, range: RangeId): Bar[] {
  const months = sliceRange(stats.readers.months, range)
  const form: MonthForm = months.length > 6 ? 'letter' : 'short'
  return bars(
    months.map(m => ({
      key: m.month,
      axis: monthLabel(m.month, form),
      value: sentCount(m.readers),
      cur: m.month === stats.month,
    }))
  )
}

/** The last 14 days' bars, today (the last) light. */
export function dayBars(stats: Stats): Bar[] {
  const { days } = stats.readers
  return bars(
    days.map((d, i) => ({
      key: d.day,
      axis: '',
      value: sentCount(d.readers) ?? 0,
      cur: i === days.length - 1,
    }))
  )
}

/** "Readers per month: Sep 2026, 2,007; Oct 2026, 3,800 so far." — the chart's spoken form. */
export function monthChartLabel(barsShown: readonly Bar[]): string {
  const parts = barsShown.map(b => {
    const name = monthLabel(b.key, 'shortYear')
    if (b.isNull) {
      return `${name}, not recorded`
    }
    return `${name}, ${fmtCount(b.value)}${b.cur ? ' so far' : ''}`
  })
  return `Readers per month: ${parts.join('; ')}.`
}

export function dayChartLabel(barsShown: readonly Bar[]): string {
  const parts = barsShown.map(b => `${b.cur ? 'today' : dayLabel(b.key)}, ${fmtCount(b.value)}`)
  return `Readers per day, last 14 days: ${parts.join('; ')}.`
}

// ── who is speaking ──────────────────────────────────────────────────────────

export interface Voice {
  own: boolean
  handle: string
}

/** "Your" / "nimble_birch_87’s". */
export function possessive({ own, handle }: Voice): string {
  return own ? 'Your' : `${handle}’s`
}

// ── the Readers card ─────────────────────────────────────────────────────────

/** Reviews whose publish day falls in `month`. */
function reviewsIn(stats: Stats, month: string): StatsReview[] {
  return stats.reviews.filter(r => monthOfDay(r.published_at) === month)
}

/**
 * The sentence under the readers figures (design 1b / 1c / 1d / 1i), second person for the
 * member, third for everyone else:
 * - one review: "Your TOZO earbuds owner review has reached 705 readers since Oct 5, about 118 a
 *   day. The community median for a single review is 746."
 * - several: "Your 2 owner reviews have reached 5,807 readers. 3,800 of them came in October, from
 *   reviews you posted in September."
 * - 6+ months: "… You were active in 2 of the last 12 months; readers came every month."
 */
export function heroSentence(stats: Stats, voice: Voice): string {
  const { own, handle } = voice
  const total = fmtCount(sentCount(stats.readers.all_time) ?? 0)
  const n = stats.reviews.length
  const whose = possessive(voice)
  if (n === 1) {
    const [r] = stats.reviews
    const since = dayLabel(r.published_at)
    const pace = perDay(r.per_day)
    const base = `${whose} ${r.name} owner review has reached ${total} readers${
      since ? ` since ${since}` : ''
    }${
      pace && r.per_day >= 1 ? `, about ${Math.round(r.per_day).toLocaleString('en-US')} a day` : ''
    }.`
    const median = sentCount(stats.community.median_readers_per_review)
    return median !== null
      ? `${base} The community median for a single review is ${fmtCount(median)}.`
      : base
  }
  const parts = [
    `${whose} ${plural(n, 'owner review', 'owner reviews')} have reached ${total} readers.`,
  ]
  const span = monthsBetween(stats.since, stats.month) ?? 0
  const monthSoFar = sentCount(stats.readers.month_so_far) ?? 0
  if (span >= 6) {
    const window = stats.months.slice(-12)
    const active = window.filter(monthActive).length
    const recorded = stats.readers.months.slice(-12).filter(m => typeof m.readers === 'number')
    const everyMonth = recorded.length > 0 && recorded.every(m => (m.readers as number) > 0)
    const who = own ? 'You were' : `${handle} was`
    parts.push(
      `${who} active in ${active} of the last ${window.length} months${
        everyMonth ? '; readers came every month' : ''
      }.`
    )
  } else if (monthSoFar > 0 && n > 1) {
    const monthName = monthLabel(stats.month, 'long')
    const publishMonths = Array.from(
      new Set(stats.reviews.map(r => monthOfDay(r.published_at)).filter(Boolean))
    )
    const allBefore = reviewsIn(stats, stats.month).length === 0
    let from = ''
    if (allBefore && publishMonths.length === 1) {
      from = `, from reviews ${own ? 'you' : 'they'} posted in ${monthLabel(
        publishMonths[0] as string,
        'long'
      )}`
    }
    parts.push(`${fmtCount(monthSoFar)} of them came in ${monthName}${from}.`)
  }
  return parts.join(' ')
}

/** "Oct so far · 3,800" — the per-month chart's header figure. */
export function monthSoFarLine(stats: Stats): string {
  return `${monthLabel(stats.month)} so far · ${fmtCount(
    sentCount(stats.readers.month_so_far) ?? 0
  )}`
}

/** "Today so far · 180". */
export function todaySoFarLine(stats: Stats): string {
  const last = stats.readers.days[stats.readers.days.length - 1]
  return `Today so far · ${fmtCount(sentCount(last?.readers) ?? 0)}`
}

/** "4,857 readers" — the 14 days summed. */
export function daysSum(stats: Stats): string {
  const sum = stats.readers.days.reduce((acc, d) => acc + (sentCount(d.readers) ?? 0), 0)
  return plural(sum, 'reader', 'readers')
}

/** "Best day Oct 1 · 412 readers" (", the day it went live" when a review was published that
 *  day); "No readers in the last 14 days" when every day is zero. */
export function bestDayNote(stats: Stats): string {
  let best: { day: string; readers: number } | null = null
  stats.readers.days.forEach(d => {
    const readers = sentCount(d.readers) ?? 0
    if (readers > 0 && (!best || readers > best.readers)) {
      best = { day: d.day, readers }
    }
  })
  const found = best as { day: string; readers: number } | null
  if (!found) {
    return 'No readers in the last 14 days'
  }
  const live = stats.reviews.some(r => r.published_at.slice(0, 10) === found.day)
  return `Best day ${dayLabel(found.day)} · ${plural(found.readers, 'reader', 'readers')}${
    live ? ', the day it went live' : ''
  }`
}

/**
 * The line under the per-month bars: which shown months are striped (design 1d), else when the
 * reviews went live — "Both reviews went live Sep 18." / "The review went live Oct 5." — when they
 * share a day. Null when there is nothing to say.
 */
export function monthNote(stats: Stats, shown: readonly Bar[], voice: Voice): string | null {
  const striped = shown.filter(b => b.isNull).map(b => monthLabel(b.key, 'shortYear'))
  if (striped.length) {
    const list =
      striped.length === 1
        ? striped[0]
        : `${striped.slice(0, -1).join(', ')} and ${striped[striped.length - 1]}`
    return `Striped: ${list} readers weren’t kept month by month. They are included in the all-time total.`
  }
  const days = Array.from(new Set(stats.reviews.map(r => r.published_at.slice(0, 10))))
  if (days.length !== 1 || !dayLabel(days[0])) {
    return null
  }
  const n = stats.reviews.length
  let who = `${possessive(voice)} review`
  if (n === 2) {
    who = 'Both reviews'
  } else if (n > 2) {
    who = `All ${n} reviews`
  }
  return `${who} went live ${dayLabel(days[0])}.`
}

// ── time spent reading ───────────────────────────────────────────────────────

/** The three states of every reading-time figure (contract Amendment B.1): the average; still
 *  collecting since a day (no average until 5 timed readers in 90 days); nothing timed yet. */
export type ReadingTimeState =
  | { kind: 'figure'; figure: string; median: string | null }
  | { kind: 'collecting'; figure: 'Collecting'; line: string }
  | { kind: 'soon'; line: string }

export const READING_TIME_SOON = 'Reading time starts counting soon.'

/**
 * B.1, one rule for every surface that prints a reading-time figure (the Stats tab card, the
 * avatar menu's tile): the figure when DealHunt has an average — with the community median when
 * it has one; else "Collecting" and the day measuring began ("Oct 12", hand-parsed UTC — never
 * Intl), the visitor's line naming whose reviews; else "starts counting soon", no date. An absent
 * `collecting_since` (a DealHunt before v2) reads as null.
 */
export function readingTimeState(
  readingTime: Pick<Stats['reading_time'], 'avg_seconds_90d' | 'collecting_since'>,
  medianSeconds: number | null | undefined,
  voice: Voice
): ReadingTimeState {
  const figure = fmtSeconds(readingTime.avg_seconds_90d)
  if (figure !== null) {
    return { kind: 'figure', figure, median: fmtSeconds(medianSeconds) }
  }
  const since = dayLabel(readingTime.collecting_since ?? null)
  if (!since) {
    return { kind: 'soon', line: READING_TIME_SOON }
  }
  const after = voice.own ? '5 timed readers' : `5 timed readers of ${voice.handle}’s reviews`
  return {
    kind: 'collecting',
    figure: 'Collecting',
    line: `Reading time has been measured since ${since}. An average shows after ${after}.`,
  }
}

export interface CompareRow {
  label: string
  value: string
  /** Bar fill, 0–100, against the larger of the two. */
  pct: number
  tone: 'you' | 'community'
}

/** "Your reviews" vs "Community median" — the second row only when DealHunt has a median. */
export function readingCompare(stats: Stats, voice: Voice): CompareRow[] {
  const you = stats.reading_time.avg_seconds_90d
  if (typeof you !== 'number') {
    return []
  }
  const comm = stats.community.median_read_seconds
  const max = Math.max(1, you, typeof comm === 'number' ? comm : 0)
  const rows: CompareRow[] = [
    {
      label: voice.own ? 'Your reviews' : 'Their reviews',
      value: fmtSeconds(you) as string,
      pct: Math.round((you / max) * 100),
      tone: 'you',
    },
  ]
  if (typeof comm === 'number') {
    rows.push({
      label: 'Community median',
      value: fmtSeconds(comm) as string,
      pct: Math.round((comm / max) * 100),
      tone: 'community',
    })
  }
  return rows
}

/**
 * The note under the comparison: per review when DealHunt timed them — "Your Sony WH-1000XM5
 * review holds readers for 1m 41s, the CMF Buds review for 49s." — else how the figure is kept;
 * without an average, the B.1 line (`readingTimeState`).
 */
export function readingNote(stats: Stats, voice: Voice): string {
  const timed = stats.reviews
    .filter(r => typeof r.avg_read_seconds_90d === 'number')
    .sort((a, b) => (sentCount(b.views_total) ?? 0) - (sentCount(a.views_total) ?? 0))
  const state = readingTimeState(stats.reading_time, stats.community.median_read_seconds, voice)
  if (state.kind !== 'figure') {
    return state.line
  }
  if (timed.length === 0) {
    return 'Time counts while a review is on screen, over the last 90 days.'
  }
  const [first, second] = timed
  const lead = `${possessive(voice)} ${first.name} review holds readers for ${fmtSeconds(
    first.avg_read_seconds_90d
  )}`
  return second
    ? `${lead}, the ${second.name} review for ${fmtSeconds(second.avg_read_seconds_90d)}.`
    : `${lead} on average.`
}

// ── readers by owner review ──────────────────────────────────────────────────

/** The tile letters live in `model.ts` (the member hover card draws them too). */
export { tileLetters }

export interface ReviewRow {
  noteId: string
  name: string
  slug: string | null
  /** "EB" — the tile's letters when there is no product image. */
  abbr: string
  imageUrl: string | null
  /** "Earbuds · Oct 5 · with photo". */
  meta: string
  readers: string
  /** "~118 / day · 1m 12s read". */
  pace: string
  /** Bar fill against the most-read review, 0–100. */
  pct: number
}

function photoLine(review: StatsReview): string {
  if ((sentCount(review.image_count) ?? 0) > 0 || review.photo_status === 'verified') {
    return 'with photo'
  }
  return 'no photo yet'
}

/** Rows sorted by readers (DealHunt sorts; sorted again so a reorder upstream cannot shuffle). */
export function reviewRows(stats: Stats): ReviewRow[] {
  const sorted = stats.reviews
    .slice()
    .sort((a, b) => (sentCount(b.views_total) ?? 0) - (sentCount(a.views_total) ?? 0))
  const max = Math.max(1, ...sorted.map(r => sentCount(r.views_total) ?? 0))
  const thisYear = parseMonth(stats.month)?.year
  return sorted.map(r => {
    const views = sentCount(r.views_total) ?? 0
    const published = parseDay(r.published_at)
    const when = dayLabel(r.published_at, published !== null && published.year !== thisYear)
    const pace = [perDay(r.per_day), fmtSeconds(r.avg_read_seconds_90d)]
    return {
      noteId: r.note_id,
      name: r.name,
      slug: r.slug,
      abbr: tileLetters(r),
      imageUrl: r.image_url,
      meta: [r.category?.name, when, photoLine(r)].filter(Boolean).join(' · '),
      readers: fmtCount(views),
      pace: [pace[0], pace[1] ? `${pace[1]} read` : null].filter(Boolean).join(' · '),
      pct: Math.round((views / max) * 100),
    }
  })
}

/** Own-only: "A photo adds +15 pts to this review." when a review has no photo and DealHunt's
 *  table prices the photo; null otherwise. The figure is the summary's (`points.photo_verified`). */
export function photoNote(stats: Stats, photoPoints: number | null): string | null {
  const pts = sentCount(photoPoints)
  if (!pts) {
    return null
  }
  const without = stats.reviews.filter(r => photoLine(r) === 'no photo yet')
  if (without.length === 0) {
    return null
  }
  return without.length === 1 && stats.reviews.length === 1
    ? `A photo adds +${pts} pts to this review.`
    : `A photo adds +${pts} pts to each review without one.`
}

// ── points & rank ────────────────────────────────────────────────────────────

export interface BreakdownRow {
  key: string
  label: string
  value: string
  pct: number
}

const BREAKDOWN_LABELS: ReadonlyArray<[keyof Stats['points']['breakdown'], string, string]> = [
  ['reviews', 'Owner review', 'Owner reviews'],
  ['first_review_bonus', 'First owner on a product', 'First owner on a product'],
  // "Photo +15" is paid at publish, no moderator in the loop (DealHunt `earnsPhotoBonus`): never
  // "verified" in reader copy; the key keeps its name.
  ['photo_verified', 'Photo', 'Photos'],
  ['review_updates', 'Review update', 'Review updates'],
  ['replies', 'Replies', 'Replies'],
  ['helpful', 'Helpful votes', 'Helpful votes'],
  ['conversation_starter', 'Conversation Starter', 'Conversation Starter'],
  ['first_responder', 'First Responder', 'First Responder'],
]

/** "Where your points came from": DealHunt's parts above zero, in a fixed order. */
export function breakdownRows(stats: Stats): BreakdownRow[] {
  const b = stats.points.breakdown ?? {}
  const parts = BREAKDOWN_LABELS.map(([key, one, many]) => ({
    key,
    label: stats.reviews.length === 1 ? one : many,
    v: sentCount(b[key]) ?? 0,
  })).filter(p => p.v > 0)
  const max = Math.max(1, ...parts.map(p => p.v))
  return parts.map(p => ({
    key: p.key,
    label: p.label,
    value: `+${p.v.toLocaleString('en-US')}`,
    pct: Math.round((p.v / max) * 100),
  }))
}

export interface PointsCard {
  total: string
  /** "525 to Expert"; null at the top tier. */
  toNext: string | null
  /** 0–100 — the hub's own progress rule (`tierProgress`). */
  pct: number
  rankAll: string
  rankMonth: string
  monthName: string
}

/** "#15 of 29" — a rank out of its board; null rank → `none`. */
function rankOf(rank: number | null, total: number | null, none: string): string {
  const r = sentCount(rank)
  if (!r) {
    return none
  }
  const t = sentCount(total)
  return t ? `#${fmtCount(r)} of ${fmtCount(t)}` : `#${fmtCount(r)}`
}

export function pointsCard(stats: Stats): PointsCard {
  const p = stats.points
  const total = sentCount(p.total) ?? 0
  return {
    total: fmtCount(total),
    toNext: toNextTierLine(p.next_tier),
    pct: tierProgress(total, p.next_tier?.points_to_go ?? null),
    rankAll: rankOf(p.rank_all, p.ranked_total_all, 'Not ranked yet'),
    rankMonth: rankOf(p.rank_month, p.ranked_total_month, 'Not ranked'),
    monthName: monthLabel(stats.month, 'long'),
  }
}

/**
 * Own-only, unranked this month: "Readers keep coming, but points this month come from new
 * activity. One reply (+10) puts you on the October board." — any points rank (DealHunt
 * 8e66e754), so one reply is enough; never a "N pts to get on" threshold (Rejected 35cf845b).
 * The +N is the summary's; without it the sentence names no figure.
 */
export function rankHint(stats: Stats, replyPoints: number | null): string | null {
  if (sentCount(stats.points.rank_month)) {
    return null
  }
  const monthName = monthLabel(stats.month, 'long')
  const reply = sentCount(replyPoints)
  const readersCame = (sentCount(stats.readers.month_so_far) ?? 0) > 0
  const lead = readersCame
    ? 'Readers keep coming, but points this month come from new activity.'
    : 'Points this month come from new activity.'
  const act = reply ? `One reply (+${reply})` : 'One reply or one owner review'
  return `${lead} ${act} puts you on the ${monthName} board.`
}

// ── gear vs other owners ─────────────────────────────────────────────────────

export interface GearChip {
  id: string
  text: string
  tone: 'better' | 'expected' | 'worse'
}

export interface GearRow {
  noteId: string
  name: string
  /** "3 owners reviewed it after you" / "7 owner reviews". */
  others: string
  /** "yes" / "no"; null when the review did not answer. */
  buyAgain: 'yes' | 'no' | null
  /** "82%" and its bar; null below DealHunt's minimum sample. */
  communityPct: number | null
  chips: GearChip[]
}

/**
 * One row per review: buy-again vs the community, the trait reads. `labels` maps a review's
 * read ids to their names (from the public member payload's feed items — the stats payload sends
 * ids only); a read without a name draws no chip, never its raw id.
 */
export function gearRows(
  stats: Stats,
  labels: ReadonlyMap<string, ReadonlyMap<string, string>>,
  voice: Voice
): GearRow[] {
  return stats.reviews.map(r => {
    const after = sentCount(r.community?.owners_after_you) ?? 0
    const total = sentCount(r.community?.owners_total) ?? 0
    const others =
      after > 0
        ? `${plural(after, 'owner', 'owners')} reviewed it after ${
            voice.own ? 'you' : voice.handle
          }`
        : plural(total, 'owner review', 'owner reviews')
    const names = labels.get(r.note_id)
    const chips: GearChip[] = []
    Object.entries(r.reads ?? {}).forEach(([id, value]) => {
      const label = names?.get(id)
      if (label && (value === 'better' || value === 'expected' || value === 'worse')) {
        chips.push({ id, text: `${label} · ${value}`, tone: value })
      }
    })
    const pct = r.community?.buy_again_pct
    let buyAgain: GearRow['buyAgain'] = null
    if (r.buy_again === true) {
      buyAgain = 'yes'
    } else if (r.buy_again === false) {
      buyAgain = 'no'
    }
    return {
      noteId: r.note_id,
      name: r.name,
      others,
      buyAgain,
      communityPct: typeof pct === 'number' && pct >= 0 && pct <= 100 ? Math.round(pct) : null,
      chips,
    }
  })
}

/** "4 owner reviews posted on your gear since yours" — Σ owners_after_you; null at 0. */
export function followersLine(stats: Stats, voice: Voice): { figure: string; text: string } | null {
  const sum = stats.reviews.reduce(
    (acc, r) => acc + (sentCount(r.community?.owners_after_you) ?? 0),
    0
  )
  if (!sum) {
    return null
  }
  const gear = voice.own ? 'your gear since yours' : `${voice.handle}’s gear since theirs`
  return {
    figure: fmtCount(sum),
    text: `${sum === 1 ? 'owner review' : 'owner reviews'} posted on ${gear}`,
  }
}

// ── month by month ───────────────────────────────────────────────────────────

/** A month counts as active when anything was scored or done in it. */
export function monthActive(m: StatsMonth): boolean {
  return (
    (sentCount(m.reviews) ?? 0) +
      (sentCount(m.updates) ?? 0) +
      (sentCount(m.replies) ?? 0) +
      (sentCount(m.helpful) ?? 0) +
      (sentCount(m.points) ?? 0) >
    0
  )
}

export interface FoldInput {
  month: string
  active: boolean
  /** null = not recorded. */
  readers: number | null
}

export interface FoldGroup {
  /** Oldest and newest month of the group (the same for a single month). */
  from: string
  to: string
  months: string[]
  kind: 'current' | 'active' | 'quiet'
  /** Σ known readers; null when every month is not recorded. */
  readers: number | null
}

/**
 * The ledger's rows, newest first (design 1d): the running month and every active month on their
 * own; consecutive quiet months fold into one row. A not-recorded month never folds with recorded
 * ones (its readers are unknown, theirs are not), so "Oct 2026 · not recorded" stays its own row.
 */
export function foldMonths(months: readonly FoldInput[], current: string): FoldGroup[] {
  const groups: FoldGroup[] = []
  const newestFirst = months.slice().reverse()
  newestFirst.forEach(m => {
    let kind: FoldGroup['kind'] = m.active ? 'active' : 'quiet'
    if (m.month === current) {
      kind = 'current'
    }
    const last = groups[groups.length - 1]
    const recorded = m.readers !== null
    if (kind === 'quiet' && last && last.kind === 'quiet' && (last.readers !== null) === recorded) {
      last.from = m.month
      last.months.push(m.month)
      if (recorded) {
        last.readers = (last.readers ?? 0) + (m.readers as number)
      }
      return
    }
    groups.push({ from: m.month, to: m.month, months: [m.month], kind, readers: m.readers })
  })
  return groups
}

export interface LedgerRow {
  key: string
  /** "Oct 2026 · so far", "Apr – Jun 2027". */
  month: string
  /** "1 owner review · TOZO earbuds", "3 quiet months". */
  what: string
  /** "705 readers", "not recorded". */
  readers: string
  /** "+75 pts · #4", "0 pts". */
  pts: string
  /** Drawn on the quiet tint (design: folded rows and a quiet running month). */
  quiet: boolean
  notRecorded: boolean
}

export const LEDGER_FOOTNOTE =
  'Points and ranks for past months are recalculated from today’s data and can shift if a review or vote is removed.'

function activityLine(stats: Stats, m: StatsMonth): string[] {
  const parts: string[] = []
  const reviews = sentCount(m.reviews) ?? 0
  if (reviews > 0) {
    const named = reviewsIn(stats, m.month)
    parts.push(
      reviews === 1 && named.length === 1
        ? `1 owner review · ${named[0].name}`
        : plural(reviews, 'owner review', 'owner reviews')
    )
  }
  const updates = sentCount(m.updates) ?? 0
  if (updates > 0) {
    parts.push(plural(updates, 'review update', 'review updates'))
  }
  const replies = sentCount(m.replies) ?? 0
  if (replies > 0) {
    parts.push(`${plural(replies, 'reply', 'replies')} to other owners`)
  }
  const helpful = sentCount(m.helpful) ?? 0
  if (helpful > 0) {
    parts.push(`${helpful.toLocaleString('en-US')} Helpful`)
  }
  return parts
}

/**
 * The Month by month ledger, newest first. `joinedAt` is the member payload's ISO date (the
 * month it falls in reads "Joined"; a month before it, "Before you joined").
 */
export function ledgerRows(
  stats: Stats,
  voice: Voice,
  joinedAt: string | null | undefined
): LedgerRow[] {
  const readersByMonth = new Map(stats.readers.months.map(m => [m.month, sentCount(m.readers)]))
  const byMonth = new Map(stats.months.map(m => [m.month, m]))
  const joined = monthOfDay(joinedAt ?? null)
  const inputs: FoldInput[] = stats.months.map(m => ({
    month: m.month,
    active: monthActive(m),
    readers: readersByMonth.has(m.month) ? (readersByMonth.get(m.month) as number | null) : null,
  }))
  return foldMonths(inputs, stats.month).map(g => {
    const notRecorded = g.readers === null
    const readers = notRecorded ? 'not recorded' : plural(g.readers as number, 'reader', 'readers')
    if (g.months.length > 1) {
      return {
        key: `${g.from}_${g.to}`,
        month: monthSpanLabel(g.from, g.to),
        what: `${g.months.length} quiet months`,
        readers,
        pts: '0 pts',
        quiet: true,
        notRecorded,
      }
    }
    const m = byMonth.get(g.from) as StatsMonth
    const parts = activityLine(stats, m)
    const before = joined !== null && (monthsBetween(g.from, joined) ?? 0) > 0
    if (joined === g.from) {
      parts.unshift('Joined')
    }
    let what = parts.join(' · ')
    if (!what) {
      if (before) {
        what = voice.own ? 'Before you joined' : `Before ${voice.handle} joined`
      } else {
        what = g.kind === 'current' ? 'No new activity' : 'Quiet month'
      }
    }
    const points = sentCount(m.points) ?? 0
    const rank = sentCount(m.rank)
    return {
      key: g.from,
      month: `${monthLabel(g.from, 'shortYear')}${g.kind === 'current' ? ' · so far' : ''}`,
      what,
      // Before the member joined, DealHunt sends 0 (contract §2 null rules): a dash, not "0 readers".
      readers: before && !g.readers ? '—' : readers,
      pts:
        points > 0 ? `+${points.toLocaleString('en-US')} pts${rank ? ` · #${rank}` : ''}` : '0 pts',
      quiet: g.kind !== 'active' && !monthActive(m),
      notRecorded: notRecorded && !before,
    }
  })
}

// ── the fresh state (design 1a) ──────────────────────────────────────────────

/** No counted review yet: the fresh board instead of empty charts. */
export function isFresh(stats: Stats): boolean {
  return stats.reviews.length === 0
}

export interface FreshState {
  /** "about 700" first-week readers: the community's median first week, else its median per
   *  review; null when DealHunt has neither. */
  expected: number | null
  /** "A first owner review here typically reaches about 700 readers in its first week. Your stats
   *  start the day it goes live." */
  sentence: string
}

export function freshState(stats: Stats, voice: Voice): FreshState | null {
  if (!isFresh(stats)) {
    return null
  }
  const median =
    sentCount(stats.community.median_first_week_readers) ??
    sentCount(stats.community.median_readers_per_review)
  const expected = median !== null && median > 0 ? roughly(median) : null
  const typical =
    expected !== null
      ? `A first owner review here typically reaches about ${fmtCount(
          expected
        )} readers in its first week. `
      : ''
  let start = `${voice.handle}’s stats start the day their first owner review goes live.`
  if (voice.own) {
    start =
      expected !== null
        ? 'Your stats start the day it goes live.'
        : 'Your stats start the day your first owner review goes live.'
  }
  return { expected, sentence: `${typical}${start}` }
}

export interface NextBadge {
  badge: CommunityBadge
  name: string
  /** "Owner reviews · 0 of 1" — the first step not done, else DealHunt's `detail`. */
  line: string
}

/**
 * Own-only "Next badge" on the fresh board: Closest to unlock's head (`closestBadges` with the
 * pinned ones first, sold-out never) — the same pick the Overview makes, so the two never
 * disagree. Locked badges never reach a visitor (Constraint ad49f9b6).
 */
export function nextBadge(
  badges: readonly CommunityBadge[],
  standing: CommunityStanding | null,
  caps: Partial<Record<CommunityBadgeId, BadgeCap>> | null
): NextBadge | null {
  const [head] = closestBadges(badges, { limit: 1, pinned: closestPinned(standing), caps })
  if (!head) {
    return null
  }
  const rows = badgeProgressRows(head.badge)
  const step = rows?.find(r => !r.done) ?? rows?.[0]
  const detail = typeof head.badge.detail === 'string' ? head.badge.detail.trim() : ''
  return {
    badge: head.badge,
    name: BADGE_LOOKS[head.badge.id].name,
    line: step
      ? `${step.label} · ${step.have.toLocaleString('en-US')} of ${step.need.toLocaleString(
          'en-US'
        )}`
      : detail,
  }
}
